import {
  ARAgingBucketName,
  ARAgingPayerType,
  type ARAgingSummaryReport,
} from '@services/api/billing/analytics.api'

export type BillingAgingPeriod = {
  label: string
  topBarValue: number
  bottomBarValue: number
  patientAmount: number
  insuranceAmount: number
  topBarProgressColor?: string
  bottomBarProgressColor?: string
}

const EMPTY_AGING_PERIODS: BillingAgingPeriod[] = [
  {
    label: '0-30 days',
    topBarValue: 0,
    bottomBarValue: 0,
    patientAmount: 0,
    insuranceAmount: 0,
  },
  {
    label: '31-60 days',
    topBarValue: 0,
    bottomBarValue: 0,
    patientAmount: 0,
    insuranceAmount: 0,
  },
  {
    label: '61-90 days',
    topBarValue: 0,
    bottomBarValue: 0,
    patientAmount: 0,
    insuranceAmount: 0,
  },
  {
    label: '90+ days',
    topBarValue: 0,
    bottomBarValue: 0,
    patientAmount: 0,
    insuranceAmount: 0,
  },
]

export function calculateArPercentage(
  amount: number | undefined,
  totalValue: number,
): number {
  if (!amount || totalValue === 0) return 0
  return Math.round((amount / totalValue) * 100)
}

export function resolveAgingSummary(
  report: ARAgingSummaryReport | null,
  patientId?: string,
  clinicId?: string,
): ARAgingSummaryReport[number] | null {
  if (!report || report.length === 0) return null

  const exactMatch = report.find(
    (summary) =>
      (!patientId || summary.patientId === patientId) &&
      (!clinicId || summary.clinicId === clinicId),
  )
  return exactMatch ?? report[0] ?? null
}

export function resolveArAgingTotal(
  report: ARAgingSummaryReport | null,
  patientId?: string,
  clinicId?: string,
): number {
  const summary = resolveAgingSummary(report, patientId, clinicId)
  return summary?.grandTotalRemaining ?? 0
}

export function calculateAgingPeriods(
  arAgingReport: ARAgingSummaryReport | null,
  patientId?: string,
  clinicId?: string,
): BillingAgingPeriod[] {
  const summary =
    patientId || clinicId
      ? resolveAgingSummary(arAgingReport, patientId, clinicId)
      : (arAgingReport?.[0] ?? null)

  if (!summary) {
    return EMPTY_AGING_PERIODS
  }

  const grandTotalRemaining = summary.grandTotalRemaining || 0

  if (grandTotalRemaining <= 0) {
    return EMPTY_AGING_PERIODS
  }

  const getBucket = (
    name: ARAgingBucketName,
    payerType: ARAgingPayerType,
  ): (typeof summary.buckets)[number] =>
    summary.buckets.find(
      (b) => b.bucket === name && b.payerType === payerType,
    ) || {
      bucket: name,
      payerType,
      totalCreated: 0,
      totalRemaining: 0,
      appointmentCount: 0,
    }

  const buildPeriod = (
    label: string,
    bucketName: ARAgingBucketName,
  ): BillingAgingPeriod => {
    const patientBucket = getBucket(bucketName, ARAgingPayerType.Patient)
    const insuranceBucket = getBucket(bucketName, ARAgingPayerType.Insurance)

    return {
      label,
      topBarValue: calculateArPercentage(
        patientBucket.totalRemaining,
        patientBucket.totalCreated,
      ),
      bottomBarValue: calculateArPercentage(
        insuranceBucket.totalRemaining,
        insuranceBucket.totalCreated,
      ),
      patientAmount: patientBucket.totalRemaining,
      insuranceAmount: insuranceBucket.totalRemaining,
    }
  }

  return [
    buildPeriod('0-30 days', ARAgingBucketName.Bucket_0_30),
    buildPeriod('31-60 days', ARAgingBucketName.Bucket_31_60),
    buildPeriod('61-90 days', ARAgingBucketName.Bucket_61_90),
    buildPeriod('90+ days', ARAgingBucketName.Bucket_90_Plus),
  ]
}
